import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LoadingButton from "./LoadingButton";
import {
  IconButton,
  useMediaQuery,
  useTheme,
  DialogContentText,
  Divider,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";

export interface ConfirmDialogProps {
  title: string;
  helperText?: string;
  confirmText?: string;
  okText?: string;
  cancelText?: string;
  onCancel?: () => Promise<void>;
  noCancel?: boolean;
  onOk: () => Promise<unknown>;
  open: boolean;
  element?: React.ReactNode;
}

let resolve: (value?: unknown) => void, reject: (reason?: any) => void;

export default function ConfirmDialog() {
  const [dialog, setDialog] = React.useState<ConfirmDialogProps>({
    title: "",
    open: false,
    onOk: async () => {},
  });

  const [okDisabled, setOkDisabled] = React.useState(false);

  const onCancel = async () => {
    if (!dialog.onCancel) {
      setDialog({ ...dialog, open: false });
      reject("canceled");
      return;
    }
    try {
      await dialog.onCancel();
      setDialog({ ...dialog, open: false });
      reject("canceled");
    } catch (error) {
      console.error(error);
      reject(error);
      // todo display error message
    }
  };

  const onOk = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      var res = await dialog.onOk();
      setDialog({ ...dialog, open: false });
      resolve(res);
    } catch (error) {
      console.error(error);
      reject(error);
      // todo display error message
    }
  };

  React.useEffect(() => {
    window.webApp = window.webApp || {};
    window.webApp.toggleConfirmDialog = (param) => {
      setDialog(param);
      if (param.confirmText) {
        setOkDisabled(true);
      }
      return new Promise((rs, rj) => {
        resolve = rs as any;
        reject = rj as any;
      });
    };
  }, []);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Dialog fullScreen={fullScreen} maxWidth={false} open={dialog.open}>
      <DialogTitle>
        {dialog.title}
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ minWidth: fullScreen ? "initial" : "480px" }}>
        <div>
          <DialogContentText component="div">
            {dialog.helperText}
            {dialog.element}
            {dialog.confirmText && (
              <>
                <Divider className="margin-t-normal margin-b-normal"></Divider>
                To confirm, please enter{" "}
                <strong>
                  <i>{dialog.confirmText}</i>
                </strong>{" "}
                in the field.
              </>
            )}
          </DialogContentText>
          {dialog.confirmText && (
            <TextField
              autoFocus
              required
              placeholder={dialog.confirmText}
              onChange={(e) => {
                setOkDisabled(e.target.value !== dialog.confirmText);
              }}
              margin="dense"
              fullWidth
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        {!dialog.noCancel && (
          <Button sx={{ mr: 4 }} onClick={onCancel}>
            {dialog.cancelText || "Cancel"}
          </Button>
        )}
        <LoadingButton disabled={okDisabled} onClick={onOk}>
          {dialog.okText || "OK"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
